import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { createButtonPressAnimation } from "../../../../utilities";
import { closeInfoPopup, setUserInterfaceDisabled } from "../../../../State/actions";
import { selectInfoPopupActive, selectInfoPopupKey, selectInfoPopupData } from "../../../../State/selectors";
import { useDimensions, ASPECT_RATIO_BREAKPOINTS } from "../../../../Contexts/DimensionsContext";
import { getNewUITexture } from "../../../../assets";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Development mode - set to true to test popup
const DEV_MODE = false;

// Overlay settings (identical to WarningPopup)
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x000000;
const OVERLAY_FADE_DURATION = 300;

// Popup animation settings (identical to WarningPopup)
const POPUP_SCALE_DURATION = 400;
const POPUP_SCALE_EASING = "easeOutBack";
const POPUP_SCALE_DELAY = 150;

// Popup panel settings (identical to WarningPopup)
const PANEL_BACKGROUND_COLOR = 0x000000;
const PANEL_BACKGROUND_ALPHA = 0.8;

// ── Button reference size (identical to WarningPopup) ────────────────────────
const BUTTON_REF_WIDTH = 160;
const BUTTON_REF_HEIGHT = 64;

// ── Fixed panel reference dimensions ─────────────────────────────────────────
// Measured at desktop scale (1.175x). Panel size is constant across all content;
// text auto-shrinks to fit. Shared with WarningPopup.
const PANEL_REF_WIDTH = 504;
const PANEL_REF_HEIGHT = 342;
const DESKTOP_SCALE_REF = 1.175;

// Info popup configuration — mirrors WarningPopup scaling system exactly
const INFO_POPUP_CONFIG = {
  REFERENCE_WIDTH: 600,

  basePaddingH: 22,
  basePaddingV: 42,
  baseMessageFontSize: 20,
  baseNumberFontSize: 28,
  baseButtonFontSize: 24,
  baseTextButtonGap: 55,
  baseBorderRadius: 20,
  baseMinEdgePadding: 20,
  baseContentWidth: 370,

  DEVICE_CONFIGS: {
    MOBILE_SMALL: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.575 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.65 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.79 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.7125 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.9 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.7 },
    },
    SMALL_DESKTOP: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.725 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.97 },
    },
    WIDE_DESKTOP: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.14 },
    },
  },
};

const InfoPopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dimensions, device, orientation, isMobileSmall } = useDimensions();

  // Redux state
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const messageKey = useSelector(selectInfoPopupKey);
  const infoPopupData = useSelector(selectInfoPopupData);

  const isOpen = DEV_MODE ? true : infoPopupActive;

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const popupContentRef = useRef(null);

  // Refs for button sprite and animation state (identical pattern to WarningPopup)
  const okButtonSpriteRef = useRef(null);
  const isOkPressAnimatingRef = useRef(false);

  // Create brightness filter for hover effect (identical to WarningPopup)
  const okBrightnessFilter = useRef(new PIXI.ColorMatrixFilter());
  okBrightnessFilter.current.brightness(2.0, false);

  // Use ref for hover state to avoid unnecessary re-renders (identical to WarningPopup)
  const isOkHoveredRef = useRef(false);

  // Parse message and extract number for special formatting
  const { textBefore, number, textAfter } = useMemo(() => {
    let messageText = "";

    if (messageKey) {
      messageText = t(messageKey);

      // Manually replace placeholders like {N}
      if (messageText && infoPopupData) {
        Object.keys(infoPopupData).forEach(key => {
          const placeholder = `{${key}}`;
          if (messageText.includes(placeholder)) {
            messageText = messageText.replace(new RegExp(`\\{${key}\\}`, 'g'), infoPopupData[key]);
          }
        });
      }
    }

    // Extract number from message
    const numberMatch = messageText.match(/\S+\s+([\d.,]+(?:\s+[A-Z]{2,})*)/);
    const cleanedMatch = numberMatch ? numberMatch[1].trim() : null;

    if (cleanedMatch) {
      const numberIndex = messageText.indexOf(cleanedMatch);
      return {
        textBefore: messageText.substring(0, numberIndex).trim(),
        number: cleanedMatch,
        textAfter: messageText.substring(numberIndex + cleanedMatch.length).trim(),
      };
    }

    return { textBefore: messageText, number: null, textAfter: "" };
  }, [messageKey, infoPopupData, t]);

  // Memoize device config — sub-classify desktop by viewport width,
  // sub-classify mobile as MOBILE_SMALL for compact phones (identical to WarningPopup)
  const deviceConfig = useMemo(() => {
    if (device === PixiSettings.DEVICE.DESKTOP) {
      let desktopSize;
      if (dimensions.width <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH) {
        desktopSize = 'SMALL_DESKTOP';
      } else if (dimensions.width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH) {
        desktopSize = 'WIDE_DESKTOP';
      } else {
        desktopSize = 'DESKTOP';
      }
      const configs = INFO_POPUP_CONFIG.DEVICE_CONFIGS;
      return (configs[desktopSize] || configs[PixiSettings.DEVICE.DESKTOP])[PixiSettings.ORIENTATION.LANDSCAPE];
    }
    if (isMobileSmall) {
      return (
        INFO_POPUP_CONFIG.DEVICE_CONFIGS.MOBILE_SMALL?.[orientation] ||
        INFO_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.MOBILE][orientation]
      );
    }
    return INFO_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
           INFO_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];
  }, [device, orientation, dimensions.width, isMobileSmall]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  // Get button texture (identical to WarningPopup)
  const buttonTexture = useMemo(
    () => getNewUITexture("bonus_card_bet_display_background"),
    []
  );

  // Popup scale — device multiplier with viewport clamping against fixed panel size
  const popupScale = useMemo(() => {
    const deviceScale = deviceConfig.scaleMultiplier || 1.0;
    const scaledW = (PANEL_REF_WIDTH / DESKTOP_SCALE_REF) * deviceScale;
    const scaledH = (PANEL_REF_HEIGHT / DESKTOP_SCALE_REF) * deviceScale;
    const maxWidth = dimensions.width * 0.85;
    const maxHeight = dimensions.height * 0.85;
    const widthRatio = scaledW > maxWidth ? maxWidth / scaledW : 1;
    const heightRatio = scaledH > maxHeight ? maxHeight / scaledH : 1;
    return deviceScale * Math.min(widthRatio, heightRatio);
  }, [deviceConfig, dimensions.width, dimensions.height]);

  // ── Fixed panel dimensions ─────────────────────────────────────────────────
  const panelWidth = useMemo(() => (PANEL_REF_WIDTH / DESKTOP_SCALE_REF) * popupScale, [popupScale]);
  const panelHeight = useMemo(() => (PANEL_REF_HEIGHT / DESKTOP_SCALE_REF) * popupScale, [popupScale]);

  // Apply popup scale to base values
  const paddingV = useMemo(() => INFO_POPUP_CONFIG.basePaddingV * popupScale, [popupScale]);
  const buttonFontSize = useMemo(() => Math.round(INFO_POPUP_CONFIG.baseButtonFontSize * popupScale), [popupScale]);
  const borderRadius = useMemo(() => INFO_POPUP_CONFIG.baseBorderRadius * popupScale, [popupScale]);
  const contentWidth = useMemo(() => INFO_POPUP_CONFIG.baseContentWidth * popupScale, [popupScale]);
  const buttonWidth = useMemo(() => BUTTON_REF_WIDTH * popupScale, [popupScale]);
  const buttonHeight = useMemo(() => BUTTON_REF_HEIGHT * popupScale, [popupScale]);

  // ── Auto-sized fonts ───────────────────────────────────────────────────────
  // Panel is fixed; text shrinks to fit within available height.
  const textAreaTopMultiplier = 1.0;
  const bottomPaddingMultiplier = 0.4;
  const textToButtonGapMultiplier = 0.3;
  // Extra lift for the button only — does NOT feed into the text-area height/
  // position calculations above, so the message text stays exactly as-is.
  const buttonExtraLiftMultiplier = 0.1;

  const { messageFontSize, numberFontSize, lineGap } = useMemo(() => {
    const textAreaTop = paddingV * textAreaTopMultiplier;
    const textAreaBottom = paddingV * bottomPaddingMultiplier + buttonHeight + paddingV * textToButtonGapMultiplier;
    const availableTextHeight = panelHeight - textAreaTop - textAreaBottom;

    let msgSize = Math.round(INFO_POPUP_CONFIG.baseMessageFontSize * popupScale);
    let numSize = Math.round(INFO_POPUP_CONFIG.baseNumberFontSize * popupScale);

    // Measure text at ideal font sizes and shrink if it overflows
    const measure = (mSize, nSize) => {
      const gap = mSize * 0.3;
      const mStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY, fontSize: mSize, fontWeight: "400",
        fill: 0xffffff, align: "center", wordWrap: true, wordWrapWidth: contentWidth,
        breakWords: true,
        ...TEXT_STYLE_CONFIG.OUTLINE,
      });
      const nStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY, fontSize: nSize, fontWeight: "700",
        fill: 0xffffff, align: "center", ...TEXT_STYLE_CONFIG.OUTLINE,
      });
      const tbUpper = textBefore.toUpperCase() || "X";
      const numUpper = number ? number.toUpperCase() : null;
      const taUpper = textAfter ? textAfter.toUpperCase() : null;
      const tbH = TextMetrics.measureText(tbUpper, mStyle).height;
      const numH = numUpper ? TextMetrics.measureText(numUpper, nStyle).height : 0;
      const taH = taUpper ? TextMetrics.measureText(taUpper, mStyle).height : 0;
      return tbH + (numUpper ? gap + numH : 0) + (taUpper ? gap + taH : 0);
    };

    const measuredHeight = measure(msgSize, numSize);
    if (measuredHeight > availableTextHeight && measuredHeight > 0) {
      const ratio = availableTextHeight / measuredHeight;
      msgSize = Math.max(10, Math.floor(msgSize * ratio));
      numSize = Math.max(12, Math.floor(numSize * ratio));
    }

    return { messageFontSize: msgSize, numberFontSize: numSize, lineGap: msgSize * 0.3 };
  }, [popupScale, panelHeight, paddingV, buttonHeight, contentWidth, textBefore, number, textAfter]);

  // Text styles
  const messageStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: messageFontSize,
    fontWeight: "400",
    fill: 0xffffff,
    align: "center",
    wordWrap: true,
    wordWrapWidth: contentWidth,
    breakWords: true,
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [messageFontSize, contentWidth]);

  const numberStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: numberFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [numberFontSize]);

  // Button text styling (identical to WarningPopup)
  const buttonTextStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: buttonFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [buttonFontSize]);

  // Text content (uppercased)
  const textBeforeUpper = textBefore.toUpperCase();
  const numberUpper = number ? number.toUpperCase() : null;
  const textAfterUpper = textAfter ? textAfter.toUpperCase() : null;

  // Text metrics
  const tbMetrics = useMemo(
    () => TextMetrics.measureText(textBeforeUpper || "X", messageStyle),
    [textBeforeUpper, messageStyle]
  );
  const numMetrics = useMemo(
    () => numberUpper ? TextMetrics.measureText(numberUpper, numberStyle) : { height: 0 },
    [numberUpper, numberStyle]
  );
  const taMetrics = useMemo(
    () => textAfterUpper ? TextMetrics.measureText(textAfterUpper, messageStyle) : { height: 0 },
    [textAfterUpper, messageStyle]
  );

  const totalTextHeight = tbMetrics.height +
    (number ? lineGap + numMetrics.height : 0) +
    (textAfterUpper ? lineGap + taMetrics.height : 0);


  // Center the panel perfectly in the middle of the screen (identical to WarningPopup)
  const panelX = useMemo(() => centerX - panelWidth / 2, [centerX, panelWidth]);
  const panelY = useMemo(() => centerY - panelHeight / 2, [centerY, panelHeight]);

  // Position content — center text vertically within the text area
  const textAreaTopY = useMemo(
    () => panelY + paddingV * textAreaTopMultiplier,
    [panelY, paddingV]
  );
  const textAreaBottomY = useMemo(
    () => panelY + panelHeight - paddingV * bottomPaddingMultiplier - buttonHeight - paddingV * textToButtonGapMultiplier,
    [panelY, panelHeight, paddingV, buttonHeight]
  );
  const textAreaCenterY = useMemo(
    () => (textAreaTopY + textAreaBottomY) / 2,
    [textAreaTopY, textAreaBottomY]
  );
  const textStartY = useMemo(
    () => textAreaCenterY - totalTextHeight / 2,
    [textAreaCenterY, totalTextHeight]
  );
  const textBeforeY = useMemo(
    () => textStartY + tbMetrics.height / 2,
    [textStartY, tbMetrics.height]
  );
  const numberY = useMemo(
    () => number ? textStartY + tbMetrics.height + lineGap + numMetrics.height / 2 : 0,
    [number, textStartY, tbMetrics.height, lineGap, numMetrics.height]
  );
  const textAfterY = useMemo(
    () => textAfterUpper
      ? textStartY + tbMetrics.height + (number ? lineGap + numMetrics.height : 0) + lineGap + taMetrics.height / 2
      : 0,
    [textAfterUpper, textStartY, tbMetrics.height, number, lineGap, numMetrics.height, taMetrics.height]
  );
  const buttonY = useMemo(
    () => panelY + panelHeight - paddingV * (bottomPaddingMultiplier + buttonExtraLiftMultiplier) - buttonHeight / 2,
    [panelY, panelHeight, paddingV, buttonHeight]
  );

  // Memoize translation texts (identical to WarningPopup)
  const okText = useMemo(() => t("ok"), [t]);

  // Memoize Graphics draw functions (identical to WarningPopup)
  const drawOverlay = useCallback((g) => {
    g.clear();
    g.beginFill(OVERLAY_BACKGROUND_COLOR);
    g.drawRect(0, 0, dimensions.width, dimensions.height);
    g.endFill();
  }, [dimensions.width, dimensions.height]);

  const drawPanel = useCallback((g) => {
    g.clear();
    g.beginFill(PANEL_BACKGROUND_COLOR, PANEL_BACKGROUND_ALPHA);
    g.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, borderRadius);
    g.endFill();
  }, [panelX, panelY, panelWidth, panelHeight, borderRadius]);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
  }, [isClosing]);

  // Hover effect handlers for OK button (identical to WarningPopup)
  const handleOkPointerOver = useCallback(() => {
    if (okButtonSpriteRef.current) {
      isOkHoveredRef.current = true;
      if (
        !okButtonSpriteRef.current.filters ||
        okButtonSpriteRef.current.filters.length === 0
      ) {
        okButtonSpriteRef.current.filters = [okBrightnessFilter.current];
      }
    }
  }, []);

  const handleOkPointerOut = useCallback(() => {
    if (okButtonSpriteRef.current) {
      isOkHoveredRef.current = false;
      okButtonSpriteRef.current.filters = [];
    }
  }, []);

  // OK button click handler with press animation (identical to WarningPopup)
  const handleOkClick = useCallback((e) => {
    e.stopPropagation();

    // Play click sound
    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before handling click
    if (okButtonSpriteRef.current && !isOkPressAnimatingRef.current) {
      isOkPressAnimatingRef.current = true;
      createButtonPressAnimation(okButtonSpriteRef.current, {
        targetWidth: buttonWidth,
        targetHeight: buttonHeight,
        onComplete: () => {
          isOkPressAnimatingRef.current = false;
          // Close popup after animation completes
          handleClose();
        }
      });
    } else {
      // If animation is already running or sprite not ready, close immediately
      handleClose();
    }
  }, [buttonWidth, buttonHeight, handleClose]);

  // Track if popup was already open when component mounted (e.g. page reload with free rounds)
  // In this case we skip animations and set final state immediately
  const wasOpenOnMount = useRef(isOpen);

  // Handle overlay fade in, popup scale up, and UI disable when opening (identical to WarningPopup)
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Disable UI when popup opens
      dispatch(setUserInterfaceDisabled(true));


      // If popup was already open on mount (page reload), set final state immediately
      if (wasOpenOnMount.current) {
        wasOpenOnMount.current = false;
        setOverlayAlpha(OVERLAY_BACKGROUND_ALPHA);
        if (popupContentRef.current) {
          popupContentRef.current.scale.set(1, 1);
        }
        return;
      }

      // 1. Fade in overlay
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
      });

      // 2. Scale up popup (with slight delay, starts during overlay fade)
      if (popupContentRef.current) {
        anime({
          targets: popupContentRef.current.scale,
          x: [0, 1],
          y: [0, 1],
          duration: POPUP_SCALE_DURATION,
          delay: POPUP_SCALE_DELAY,
          easing: POPUP_SCALE_EASING,
        });
      }
    } else if (!isOpen && !isClosing) {
      // Re-enable UI when popup closed externally (no animation needed)
      dispatch(setUserInterfaceDisabled(false));
    }
  }, [isOpen, isClosing, dispatch, panelWidth, panelHeight, popupScale, contentWidth, dimensions.width, dimensions.height, device, orientation]);

  // Handle close animation when user clicks OK (identical to WarningPopup)
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let popupAnim = null;

    // 1. Scale down popup (reverse of scale up)
    if (popupContentRef.current) {
      popupAnim = anime({
        targets: popupContentRef.current.scale,
        x: 0,
        y: 0,
        duration: POPUP_SCALE_DURATION,
        easing: "easeInBack",
      });
    }

    // 2. Fade out overlay (can run in parallel)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: 50,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(setUserInterfaceDisabled(false));
        dispatch(closeInfoPopup());
        setIsClosing(false);
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch]);

  // Clean up hover effects on unmount (identical to WarningPopup)
  useEffect(() => {
    const okSprite = okButtonSpriteRef.current;
    return () => {
      if (okSprite) {
        okSprite.filters = [];
      }
    };
  }, []);

  // Early return AFTER all hooks - stay mounted during close animation
  if (!isOpen && !isClosing) {
    return null;
  }

  return (
    <Container name="info_popup" zIndex={99} sortableChildren={true}>
      {/* Black overlay - NOT clickable to close (identical to WarningPopup) */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => {
          e.stopPropagation();
          // Do nothing - user must click OK button to close
        }}
        zIndex={99}
      />

      {/* Content container - animated scale (identical structure to WarningPopup) */}
      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={100}
        x={centerX}
        y={centerY}
        pivot={{ x: centerX, y: centerY }}
      >
        {/* Dark panel background - wraps text and button */}
        <Graphics draw={drawPanel} />

        {/* Text before number */}
        {textBeforeUpper && (
          <Text
            text={textBeforeUpper}
            style={messageStyle}
            anchor={0.5}
            x={centerX}
            y={textBeforeY}
            resolution={2}
          />
        )}

        {/* Number (bolder, larger) */}
        {numberUpper && (
          <Text
            text={numberUpper}
            style={numberStyle}
            anchor={0.5}
            x={centerX}
            y={numberY}
            resolution={2}
          />
        )}

        {/* Text after number */}
        {textAfterUpper && (
          <Text
            text={textAfterUpper}
            style={messageStyle}
            anchor={0.5}
            x={centerX}
            y={textAfterY}
            resolution={2}
          />
        )}

        {/* OK Button - single centered button (identical to WarningPopup single-button layout) */}
        <Container
          name="ok_button"
          x={centerX}
          y={buttonY}
        >
          {/* Interactive area - handles all pointer events */}
          <Graphics
            name="ok_button_interactive_area"
            eventMode="static"
            cursor="pointer"
            hitArea={new PIXI.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight)}
            onpointertap={handleOkClick}
            onpointerover={handleOkPointerOver}
            onpointerout={handleOkPointerOut}
            draw={(g) => {
              g.clear();
              // Nearly invisible fill (required for hit detection to work)
              g.beginFill(0x000000, 0.01);
              g.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
              g.endFill();
            }}
          />

          <Sprite
            ref={okButtonSpriteRef}
            texture={buttonTexture}
            anchor={0.5}
            width={buttonWidth}
            height={buttonHeight}
            eventMode="none"
          />
          <Text
            text={okText}
            style={buttonTextStyle}
            anchor={0.5}
            resolution={2}
            eventMode="none"
          />
        </Container>
      </Container>
    </Container>
  );
};

export default InfoPopup;
